import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { UserService } from '../shared/services/user.service';
import { GridDataResult, SelectAllCheckboxState } from '@progress/kendo-angular-grid';
import * as _ from 'lodash';
import { PageInfo } from '../shared/models/pageInfoModel';
import { UserSkillsResponseModel } from '../shared/models/UserSkillsResponseModel';
import { UserSkillsPostModel } from '../shared/models/userSkillsPostModel';
import { SortDescriptor } from '@progress/kendo-data-query';
import { PageChangeEvent } from '@progress/kendo-angular-grid';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { ToastService } from 'src/app/shared/services/toast.service';
import { UserSkillsGridModel } from '../shared/models/userSkillsGridModel';
import { ValueAxis } from '@progress/kendo-angular-charts';
import { TranslationLoaderFacade } from '../../localization/translation-loader.facade';
import { UserEditDetails } from '../shared/models/userEditDetailsModel';
import { Data } from '@angular/router';
import { ChipRemoveEvent } from '@progress/kendo-angular-buttons';

@Component({
  selector: 'app-user-skills',
  templateUrl: './user-skills.component.html',
  styleUrls: ['./user-skills.component.scss']
})
export class UserSkillsComponent implements OnInit {
  @Output() dirtyEmitter = new EventEmitter<boolean>(false);
  @Output() closeEmitter = new EventEmitter();

  public search: string = '';
  public category: any;
  public optStatus: number | null = null;
  public editingItem: boolean = false;
  public editingUser: UserEditDetails | null = null;
  public dataSkillGrid: UserSkillsGridModel[] = [];
  public userSkillsDataSet: UserSkillsGridModel[] = [];
  public skillsSelection: number[] = [];
  public showClearButton: boolean = false;
  public isProficiencyChanged: boolean = true;
  public selectAllState: SelectAllCheckboxState = 'unchecked';
  public isSkillsEdited: boolean | undefined;
  public isSkillsValid: boolean | undefined;
  private oldProficiency: number | undefined;
  private oldOptInStatus: boolean | undefined;

  public categoryList = [{ id: -1, displayValue: 'All' }];
  public optInOutStatusList = [
    { id: 1, displayValue: 'Opted in' }, 
    { id: -1, displayValue: 'All' }, 
    { id: 0, displayValue: 'Opted out' }
  ];
  public skip = 0;
  public pageSize = 5;
  public pageInfo: PageInfo = {
    PageSize: 0,
    Page: 0
  };
  public sort: SortDescriptor[] = [{
    field: 'serviceName',
    dir: 'asc'
  }];

  constructor(public userService: UserService, public toastService: ToastService, private translationLoaderFacade: TranslationLoaderFacade) { }

  ngOnInit() {
    this.loadUserSkills();
  }

  public loadUserSkills() {
    this.isSkillsEdited = false;
    this.isSkillsValid = true;
    this.oldProficiency = 0;
    this.oldOptInStatus = false;

    this.pageInfo = { PageSize: 25, Page: 1 };

    this.getCategoryIdAndNames().subscribe({
      next : (categories) => { 
        this.categoryList = categories.sort((a, b) => a.displayValue.localeCompare(b.displayValue));; 
      },
      error : (err) => {console.log(err)}
    });

    // unClear logic
    this.category = this.categoryList[0];
    this.category = -1;
    this.optStatus = 1;
    this.editingItem = false;

    this.editingUser = this.userService.getEditingUser();
    this.dataSkillGrid = [];
    if (this.editingUser) {
      //assigning edituser.userservice to both members
      this.userSkillsDataSet = this.dataSkillGrid = this.editingUser.userService.map(userService => {
        const userSkillsGrid = new UserSkillsGridModel();
        userSkillsGrid.userId = userService.userId;
        userSkillsGrid.optIn = userService.optIn;
        userSkillsGrid.serviceId = userService.serviceId;
        userSkillsGrid.serviceName = userService.serviceName;
        userSkillsGrid.categoryId = userService.categoryId;
        userSkillsGrid.categoryName = userService.categoryName;
        userSkillsGrid.proficiency = userService.proficiency;
        userSkillsGrid.isProficiencyInvalid = false; // setting default value to false

        return userSkillsGrid;
      });
      this.statusChange();
    }
    this.validateProficiency();
    this.optInAllStatusChange();
  }

  /**
   * 
   * Get all Active Categories
   */
  getCategoryIdAndNames(): Observable<any[]> {
    return this.userService.getProductCategoryDisplayData().pipe(map(data => {
      return data.map(item => {
        return {
          id: item.id,
          displayValue: item.categoryName
        }
      })
    }))
  }

  public onSelectAllChange(checkedState: SelectAllCheckboxState, dataItem?: any) {
    this.isSkillsEdited = true;
    if (this.dataSkillGrid == undefined) {
      throw Error("this.dataSkillGrid was undefined! Which was not expected!")
    }
    if (checkedState === 'checked') {
      this.skillsSelection = this.dataSkillGrid.map((item) => item.serviceId);

      this.selectAllState = 'checked';
      this.dataSkillGrid.forEach(element => {
        element.proficiency = 100;
        element.optIn = true;
      });
    } else {
      this.skillsSelection = [];
      this.selectAllState = 'unchecked';
      this.dataSkillGrid.forEach(element => {
        element.proficiency = 0;
        element.optIn = false;
      });
    }
    this.validateProficiency();
  }

  public onSelectedKeysChange() {
    const len = this.skillsSelection.length;
    if (len === 0) {
      this.selectAllState = 'unchecked';
    } else if (len > 0 && len < this.dataSkillGrid.length) {
      this.selectAllState = 'indeterminate';
    } else {
      this.selectAllState = 'checked';
    }
  }

  public optInAllStatusChange() {
    this.skillsSelection = (this.dataSkillGrid) ? this.dataSkillGrid.filter(skill => skill.optIn == true).map((item) => item.serviceId) : [];
    this.onSelectedKeysChange();
  }

  public proficiencyChange() {
    this.validateProficiency();
  }

  public checkedChangedEvent(event?: any, data?: any) {
    this.isProficiencyChanged = false;
    data.optIn = !data.optIn;
    if (data.optIn == false) {
      data.proficiency = 0;
    } else {
      data.proficiency = 100;
    }
    this.optInAllStatusChange();
  }

  /* 
    Edit values
  */
  public editHandler(event: Data) {
    this.isProficiencyChanged = true;
    this.editingItem = true;
    event.dataItem.editable = !event.dataItem.editable;
    this.oldProficiency = event.dataItem.proficiency;
    this.oldOptInStatus = event.dataItem.optIn;
  }

  /* 
    Inline saving
  */
  public submitUserService(dataItem: Data) {
    this.isSkillsEdited = true;
    this.editingItem = false;
    dataItem.editable = !dataItem.editable;
    this.oldProficiency = 0;
    this.oldOptInStatus = false;
    this.statusChange();
    this.applyFilters();
  }

  /* 
    Inline canceling
  */
  public cancelUserService(dataItem: Data) {
    this.editingItem = false;
    dataItem.editable = !dataItem.editable;
    dataItem.proficiency = this.oldProficiency;
    dataItem.optIn = this.oldOptInStatus;
    this.oldProficiency = 0;
    this.oldOptInStatus = false;
    this.validateProficiency();
    this.optInAllStatusChange();
  }

  /**
   * Triggers when search key word changes
   */
  public searchKeyWordChange() {
    if (this.search) {
      let searchText = this.search.toLowerCase();

      // initialize the dataSkillGrid again,
      // otherwise, for an empty-result search, the previous dataSkillGrid collection is retained and displayed
      this.dataSkillGrid = [];
      let matchRegex = new RegExp(searchText, 'gi');
      let filteredDataSource = this.userSkillsDataSet.filter(us => us.serviceName.toLowerCase().indexOf(searchText) >= 0
        || us.categoryName.toLowerCase().indexOf(searchText) >= 0
        || us.proficiency.toString().toLowerCase().indexOf(searchText) >= 0);

      filteredDataSource = filteredDataSource.filter(us => us.categoryId == this.category);
      this.dataSkillGrid = filteredDataSource;
      this.optInAllStatusChange();
    }
    else {
      this.dataSkillGrid = this.userSkillsDataSet;
    }
    this.validateProficiency();
    this.pageInfo.Page = 1;
  }

  public applyFilters() {
    // initialize the dataSkillGrid again,
    // otherwise, for an empty-result search, the previous dataSkillGrid collection is retained and displayed
    this.dataSkillGrid = [];
    let filteredDataSource;
    if (this.search) {
      let searchText = this.search.toLowerCase();
      filteredDataSource = this.userSkillsDataSet.filter(us => us.serviceName.toLowerCase().indexOf(searchText) >= 0
        || us.categoryName.toLowerCase().indexOf(searchText) >= 0
        || us.proficiency.toString().toLowerCase().indexOf(searchText) >= 0);
    }
    else {
      filteredDataSource = this.userSkillsDataSet;
    }
    filteredDataSource = filteredDataSource.filter(us => us.categoryId == this.category || this.category == -1);
    filteredDataSource = filteredDataSource.filter(us => us.optIn == (this.optStatus == 1 ? true : false) || this.optStatus == -1);
    this.dataSkillGrid = filteredDataSource;
    this.pageInfo.Page = 1;
    this.validateProficiency();
    this.optInAllStatusChange();
  }

  /**
     * Triggers when category changes
     */
  public categoryChange() {
    if (this.category > 0) {
      // initialize the dataSkillGrid again,
      // otherwise, for an empty-result search, the previous dataSkillGrid collection is retained and displayed
      this.dataSkillGrid = [];

      let filteredDataSource = this.userSkillsDataSet.filter(us => us.categoryId == this.category);
      this.dataSkillGrid = filteredDataSource;
    }
    else {
      this.dataSkillGrid = this.userSkillsDataSet;
    }
    this.pageInfo.Page = 1;
    this.validateProficiency();
  }


  /**
   * Triggers when the users changes the OptIn Status
   */
  public statusChange() {
    if (this.optStatus == null) {
      throw Error("this.optStatus was null! Which was not expected!")
    }
    if (this.optStatus >= 0) {
      // initialize the dataSkillGrid again,
      // otherwise, for an empty-result search, the previous dataSkillGrid collection is retained and displayed
      this.dataSkillGrid = [];

      let filteredDataSource = this.userSkillsDataSet.filter(us => us.optIn == (this.optStatus == 1 ? true : false));
      this.dataSkillGrid = filteredDataSource;
    }
    else {
      this.dataSkillGrid = this.userSkillsDataSet;
    }
    this.pageInfo.Page = 1;
    this.validateProficiency();
  }

  /**
   * Filter grid when pressing the cross icon in the search text box
   */
  public onClearSearchTextClicked() {
    this.search = '';
    this.onSearchTextChanges();
    this.applyFilters();
  }

  /**
     * Show hide cross icon depends on the availability of the search text
     * @param val search text
     */
  public onSearchTextChanges() {
    this.updateSearchTextClearButtonVisibility();
  }

  private updateSearchTextClearButtonVisibility() {

    if (this.search && this.search !== '') {
      this.showClearButton = true;
    } else {
      this.showClearButton = false;
    }
  }

  /**
   * Save manupulated skills grid data
   */
  public saveGridData() {
    this.userService.saveUserService(this.userSkillsDataSet).subscribe((data: UserSkillsResponseModel[]) => {
      this.toastService.showSuccess('_General_Changes_Saved_Successfully_Ts_Text_');
      this.isSkillsEdited= false;
      this.statusChange();
    },
    error => {
      console.log(error);
      this.toastService.showError('_General_Saving_Failed_Ts_Text_');
      this.statusChange();
    });

    this.category = -1;
  }


  /**
   * Upon clicking cancel persist in the state where it was before.
   */
  public cancelGridData() {
    this.closeEmitter.emit('ngb-panel-4');
  }

  public onPageChange(event: PageChangeEvent) {
    this.pageInfo.Page = (event.skip / this.pageInfo.PageSize) + 1;
  }

  public validateProficiency() {
    this.isProficiencyChanged = false;
    this.isSkillsValid = true;
    this.dataSkillGrid?.forEach(s => {
      s.proficiency = this.decimalToInteger(s.proficiency);
      s.proficiency = s.proficiency < 0 ? 0 : s.proficiency;
      s.proficiency = s.proficiency > 100 ? 100 : s.proficiency;
      if (s.proficiency == null) {
        s.isProficiencyInvalid = true;
        this.isSkillsValid = false;
      } else {
        s.isProficiencyInvalid = false;
      }
    });
  }

  public onKeyPressProficiency(event: Data, value: string) {
    this.isProficiencyChanged = false;
    var proficiency = +(value + event.key);
    var charCode = (event.which) ? event.which : event.keyCode;
    // Only Numbers 0-9
    if ((charCode < 48 || charCode > 57) || proficiency > 100) {
      event.preventDefault();
      return false;
    } else {
      return true;
    }
  }

  /**
   * Convert decimal value to integer
   * @param value 
   * @returns
   */
  private decimalToInteger(value: number) {
    return value != null ? value | 0 : value;
  }
}
